// Simple test page for backend services
import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useLenders } from '../hooks/useLenders';
import { useSeries } from '../hooks/useSeries';
import { useTotalAUM } from '../hooks/useSeries';

const TestBackend: React.FC = () => {
  const { lenders, loading: loadingLenders, error: errorLenders, addLender, removeLender } = useLenders(true);
  const { series, loading: loadingSeries, error: errorSeries, addSeries, removeSeries } = useSeries(true);
  const { totalAUM, loading: loadingAUM, refresh: refreshAUM } = useTotalAUM();
  
  const [testName, setTestName] = useState('Test Lender');
  const [testEmail, setTestEmail] = useState('test@example.com');
  const [testAmount, setTestAmount] = useState('1000000');
  const [selectedLenderId, setSelectedLenderId] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleAddTestLender = async () => {
    try {
      const id = await addLender({
        fullName: testName,
        email: testEmail,
        phone: '+852 1234 5678',
        address: '123 Test Street, Central, Hong Kong',
        idNumber: 'TEST123456',
        bankDetails: {
          bankName: 'HSBC',
          accountName: testName,
          accountNumber: '123-456789-001',
          bankCode: '004',
          branchCode: '123',
        },
        createdAt: new Date(),
        updatedAt: new Date(),
        totalInvested: 0,
      });
      setSuccessMessage(`✅ Lender created! ID: ${id}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      alert('Error: ' + err.message);
    }
  };

  const handleAddTestSeries = async () => {
    if (!selectedLenderId) {
      alert('Please select a lender first');
      return;
    }

    try {
      const principal = parseFloat(testAmount);
      const issueDate = new Date();
      const lockupEnd = new Date(issueDate);
      lockupEnd.setMonth(lockupEnd.getMonth() + 12);

      const id = await addSeries({
        lenderId: selectedLenderId,
        principalAmount: principal,
        closingDate: issueDate,
        issueDate: issueDate,
        lockupEnd: lockupEnd,
        highWaterMark: principal,
        status: 'active',
        currentNAV: principal,
        capitalizedInterest: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      setSuccessMessage(`✅ Series created! ID: ${id}`);
      setTimeout(() => setSuccessMessage(''), 3000);
      refreshAUM();
    } catch (err: any) {
      alert('Error: ' + err.message);
    }
  };

  const handleDeleteLender = async (lenderId: string) => {
    if (window.confirm('Delete this lender?')) {
      try {
        await removeLender(lenderId);
        setSuccessMessage('✅ Lender deleted!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err: any) {
        alert('Error: ' + err.message);
      }
    }
  };

  const handleDeleteSeries = async (seriesId: string) => {
    if (window.confirm('Delete this series?')) {
      try {
        await removeSeries(seriesId);
        setSuccessMessage('✅ Series deleted!');
        setTimeout(() => setSuccessMessage(''), 3000);
        refreshAUM();
      } catch (err: any) {
        alert('Error: ' + err.message);
      }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h3" gutterBottom sx={{ color: '#FFD700', mb: 3 }}>
        🧪 Backend Services Test
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {/* AUM Card */}
      <Card sx={{ bgcolor: '#1a1a1a', border: '2px solid #FFD700', mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h5" sx={{ color: '#FFD700' }}>
              📊 Total AUM
            </Typography>
            <IconButton onClick={refreshAUM} sx={{ color: '#FFD700' }}>
              <RefreshIcon />
            </IconButton>
          </Box>
          {loadingAUM ? (
            <CircularProgress sx={{ color: '#FFD700', mt: 2 }} />
          ) : (
            <Typography variant="h3" sx={{ color: 'white', mt: 2 }}>
              ${totalAUM.toLocaleString()}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Create Forms */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {/* Create Lender Form */}
        <Card sx={{ bgcolor: '#1a1a1a', border: '1px solid #333', flex: '1 1 400px' }}>
          <CardContent>
            <Typography variant="h5" sx={{ color: '#FFD700', mb: 2 }}>
              ➕ Create Test Lender
            </Typography>
            
            <TextField
              fullWidth
              label="Full Name"
              value={testName}
              onChange={(e) => setTestName(e.target.value)}
              sx={{ mb: 2 }}
              InputLabelProps={{ style: { color: '#999' } }}
              InputProps={{ style: { color: 'white' } }}
            />
            
            <TextField
              fullWidth
              label="Email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              sx={{ mb: 2 }}
              InputLabelProps={{ style: { color: '#999' } }}
              InputProps={{ style: { color: 'white' } }}
            />
            
            <Button
              fullWidth
              variant="contained"
              onClick={handleAddTestLender}
              sx={{
                bgcolor: '#FFD700',
                color: '#000',
                '&:hover': { bgcolor: '#FFC700' },
              }}
            >
              Create Lender
            </Button>
          </CardContent>
        </Card>

        {/* Create Series Form */}
        <Card sx={{ bgcolor: '#1a1a1a', border: '1px solid #333', flex: '1 1 400px' }}>
          <CardContent>
            <Typography variant="h5" sx={{ color: '#FFD700', mb: 2 }}>
              💰 Create Test Series
            </Typography>
            
            <TextField
              fullWidth
              label="Principal Amount (USD)"
              type="number"
              value={testAmount}
              onChange={(e) => setTestAmount(e.target.value)}
              sx={{ mb: 2 }}
              InputLabelProps={{ style: { color: '#999' } }}
              InputProps={{ style: { color: 'white' } }}
            />
            
            <TextField
              fullWidth
              label="Lender ID"
              value={selectedLenderId}
              onChange={(e) => setSelectedLenderId(e.target.value)}
              placeholder="Click lender ID below to copy"
              sx={{ mb: 2 }}
              InputLabelProps={{ style: { color: '#999' } }}
              InputProps={{ style: { color: 'white' } }}
            />
            
            <Button
              fullWidth
              variant="contained"
              onClick={handleAddTestSeries}
              disabled={!selectedLenderId}
              sx={{
                bgcolor: '#FFD700',
                color: '#000',
                '&:hover': { bgcolor: '#FFC700' },
              }}
            >
              Create Series
            </Button>
          </CardContent>
        </Card>
      </Box>

      {/* Data Lists */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {/* Lenders List */}
        <Card sx={{ bgcolor: '#1a1a1a', border: '1px solid #333', flex: '1 1 400px' }}>
          <CardContent>
            <Typography variant="h5" sx={{ color: '#FFD700', mb: 2 }}>
              👥 Lenders ({lenders.length})
            </Typography>
            
            {loadingLenders ? (
              <CircularProgress sx={{ color: '#FFD700' }} />
            ) : errorLenders ? (
              <Alert severity="error">{errorLenders}</Alert>
            ) : lenders.length === 0 ? (
              <Typography sx={{ color: '#999' }}>No lenders yet. Create one above!</Typography>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {lenders.map((lender) => (
                  <ListItem
                    key={lender.id}
                    sx={{
                      border: '1px solid #333',
                      mb: 1,
                      borderRadius: 1,
                      bgcolor: '#0a0a0a',
                    }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteLender(lender.id)}
                        sx={{ color: '#ff4444' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box>
                          <Typography sx={{ color: 'white' }}>{lender.fullName}</Typography>
                          <Chip
                            label={lender.id}
                            size="small"
                            sx={{ bgcolor: '#FFD700', color: '#000', fontSize: '10px', mt: 0.5 }}
                            onClick={() => {
                              setSelectedLenderId(lender.id);
                              navigator.clipboard.writeText(lender.id);
                              alert('ID copied!');
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography sx={{ color: '#999', fontSize: '12px' }}>
                          {lender.email}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>

        {/* Series List */}
        <Card sx={{ bgcolor: '#1a1a1a', border: '1px solid #333', flex: '1 1 400px' }}>
          <CardContent>
            <Typography variant="h5" sx={{ color: '#FFD700', mb: 2 }}>
              📈 Series ({series.length})
            </Typography>
            
            {loadingSeries ? (
              <CircularProgress sx={{ color: '#FFD700' }} />
            ) : errorSeries ? (
              <Alert severity="error">{errorSeries}</Alert>
            ) : series.length === 0 ? (
              <Typography sx={{ color: '#999' }}>No series yet!</Typography>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {series.map((s) => (
                  <ListItem
                    key={s.id}
                    sx={{
                      border: '1px solid #333',
                      mb: 1,
                      borderRadius: 1,
                      bgcolor: '#0a0a0a',
                    }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteSeries(s.id)}
                        sx={{ color: '#ff4444' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={
                        <Typography sx={{ color: 'white' }}>
                          ${s.principalAmount.toLocaleString()}
                        </Typography>
                      }
                      secondary={
                        <Typography sx={{ color: '#999', fontSize: '12px' }}>
                          Status: {s.status}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default TestBackend;
